/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Form, Modal, Spinner, Table,
} from 'react-bootstrap';
import BillingoService from '../../Services/BillingoService';
import { apiErrorText, docTypeLabel } from '../../Utils/billingoDocuments';
import InvoiceStatusPill from './InvoiceStatusPill';
import DocumentTotal from './DocumentTotal';

// The one confirmation before a storno, saying what it does. Two uses:
//   - `order`: a booking that was just cancelled — its still-active Billingo
//     documents are loaded and the operator picks which to storno (the
//     booking is cancelled either way; this only touches its documents);
//   - `documents`: the ones the operator asked to cancel from a document list.
// Never automatic: nothing is cancelled until "Storno selected", and a refusal
// stays on screen with Billingo's reason.
function StornoModal({
  order = null, documents: given = null, title = 'This booking was cancelled — storno its documents?',
  show = false, onHide, onDone = () => {},
}) {
  const [documents, setDocuments] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  // Only documents that are real Billingo documents and not already cancelled
  // can be storno'd.
  const isActive = (doc) => doc.billingo_document_id && doc.status !== 'cancelled';

  // The documents to offer — the given ones, or the order's, loaded whenever
  // the modal opens for it. Every active one starts checked.
  useEffect(() => {
    let cancelled = false;
    const offer = (docs) => {
      const active = (docs || []).filter(isActive);
      setDocuments(active);
      setSelected(active.reduce((acc, doc) => ({ ...acc, [doc.id]: true }), {}));
    };
    const load = async () => {
      if (!show) return;
      setError('');
      if (given) {
        offer(given);
        return;
      }
      if (!order?._id) return;
      setLoading(true);
      try {
        const docs = await BillingoService.listDocumentsForBooking(order);
        if (!cancelled) offer(docs);
      } catch (err) {
        if (cancelled) return;
        setDocuments([]);
        setError(apiErrorText(err, 'Failed to load documents'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show, order?._id, given]);

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const selectedDocs = documents.filter((doc) => selected[doc.id]);

  const handleConfirm = async () => {
    setWorking(true);
    setError('');
    try {
      // Storno each confirmed document. Sequential so a failure stops the rest
      // and surfaces a clear error rather than firing a burst of fiscal calls.
      for (let i = 0; i < selectedDocs.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await BillingoService.cancelDocument({
          billingo_document_id: selectedDocs[i].billingo_document_id,
        });
      }
      onDone();
      onHide();
    } catch (err) {
      setError(apiErrorText(err, 'Failed to storno document'));
    } finally {
      setWorking(false);
    }
  };

  // Nothing to storno → render nothing. The host can also guard, but this keeps
  // the component safe to mount unconditionally.
  if (show && !loading && documents.length === 0 && !error) return null;

  return (
    <Modal show={show} onHide={working ? undefined : onHide} backdrop={working ? 'static' : true}>
      <Modal.Header closeButton={!working}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading && (
          <div className="text-muted small">
            <Spinner size="sm" animation="border" className="me-2" />
            Loading documents…
          </div>
        )}

        {!loading && documents.length > 0 && (
          <>
            <p className="small">
              A storno is a new fiscal document that reverses the invoice: Billingo
              issues it, the original stays on record as cancelled, and the amount
              stops counting as invoiced — the order will need a new document. A
              Díjbekérő has no storno; Billingo archives it.
              {' '}
              <strong>This cannot be undone.</strong>
            </p>
            <Table responsive size="sm" className="align-middle">
              <thead className="table-light">
                <tr>
                  <th aria-label="select" />
                  <th>Type</th>
                  <th>Number</th>
                  <th className="text-end">Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={!!selected[doc.id]}
                        disabled={working}
                        onChange={() => toggle(doc.id)}
                        aria-label={`Storno ${doc.number}`}
                      />
                    </td>
                    <td>{docTypeLabel(doc.type)}</td>
                    <td>{doc.number}</td>
                    <td className="text-end"><DocumentTotal doc={doc} /></td>
                    <td><InvoiceStatusPill status={doc.status} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={working}>
          Don&apos;t storno
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={working || loading || selectedDocs.length === 0}
        >
          {working && <Spinner size="sm" animation="border" className="me-2" />}
          Storno selected
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default StornoModal;
